import {
  StudentsDeleteNoteRouteResponse,
  StudentsGetActivityLogQueryRouteResponse,
  StudentsGetAnalyticsRouteResponse,
  StudentsGetLessonsQueryRouteResponse,
  StudentsGetOutstandingInvoicesQueryRouteResponse,
  StudentsGetNotesQueryRouteResponse,
  StudentsGetQueryRouteResponse,
  StudentsGetRootRouteResponse,
  StudentsPostInvoiceNewRouteResponse,
  StudentsPostNewRouteBodyInput,
  StudentsPostNewRouteResponse,
  StudentsPostNoteNewRouteBodyInput,
  StudentsPostNoteNewRouteResponse,
  StudentsPutActivityLogUpdateRouteBodyInput,
  StudentsPutActivityLogUpdateRouteResponse,
  StudentsPutCourseStateCloseRouteBody,
  StudentsPutCourseStateCloseRouteResponse,
  StudentsPutCreditsUpdateRouteBody,
  StudentsPutCreditsUpdateRouteResponse,
  StudentsPutNoteUpdateRouteBodyInput,
  StudentsPutNoteUpdateRouteResponse,
  StudentsPutOnboardingUpdateRouteBodyInput,
  StudentsPutOnboardingUpdateRouteResponse,
  StudentsPutUpdateRouteBodyInput,
  StudentsPutUpdateRouteResponse,
} from '@repo/types/api';

import { cognitoApi } from '../api';
import { StudentAnalytics } from './types';

// Define a service using a base URL and expected endpoints
export const studentsApi = cognitoApi.injectEndpoints({
  endpoints: build => ({
    newStudent: build.mutation<
      StudentsPostNewRouteResponse,
      { attributes: StudentsPostNewRouteBodyInput }
    >({
      query(arg) {
        const { attributes } = arg;

        return {
          url: 'students/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: ['Users', 'Students'],
    }),
    getStudent: build.query<StudentsGetRootRouteResponse, { studentId: string }>({
      query: arg => `students/${arg?.studentId}`,
      providesTags: (_result, _error, arg) => [{ type: 'Students', id: arg?.studentId }],
    }),
    getStudents: build.query<
      StudentsGetQueryRouteResponse,
      {
        search?: string;
        excludeDisabled?: boolean;
        gradYear?: number;
        state?: string;
        limit?: number | 'all';
      }
    >({
      query: arg => {
        const { excludeDisabled, gradYear, search, state, limit } = arg;
        return {
          url: 'students/query',
          method: 'GET',
          params: {
            excludeDisabled,
            gradYear,
            search,
            state,
            limit,
          },
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'Students' as const, id })), 'Students']
          : ['Students'],
    }),
    updateStudent: build.mutation<
      StudentsPutUpdateRouteResponse,
      { studentId: string; newAttributes: StudentsPutUpdateRouteBodyInput }
    >({
      query(arg) {
        const { studentId, newAttributes } = arg;
        return {
          url: `students/${studentId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Invalidates the student but also all Lessons and
      // LessonEnrolments for that student if the student's
      // attendanceType is updated
      invalidatesTags: (_result, _error, arg) =>
        arg.newAttributes.attendanceType
          ? [
              { type: 'Students', id: arg.studentId },
              { type: 'LessonEnrolments', id: arg.studentId },
              'Lessons',
            ]
          : [{ type: 'Students', id: arg.studentId }],
    }),
    updateStudentOnboarding: build.mutation<
      StudentsPutOnboardingUpdateRouteResponse,
      { studentId: string; progress: StudentsPutOnboardingUpdateRouteBodyInput }
    >({
      query(arg) {
        const { studentId, progress } = arg;
        return {
          url: `students/${studentId}/onboarding/update`,
          body: progress,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'Students', id: arg.studentId }],
    }),
    getStudentAnalytics: build.query<
      StudentAnalytics,
      { studentId: string; subject: string; topicCode?: string }
    >({
      query: arg => {
        const { studentId, subject, topicCode } = arg;
        return {
          url: `students/${studentId}/analytics`,
          method: 'GET',
          params: {
            subject,
            topicCode,
          },
        };
      },
      providesTags: (_result, _error, arg) => [{ type: 'StudentAnalytics', id: arg?.studentId }],
      transformResponse(response: StudentsGetAnalyticsRouteResponse): StudentAnalytics {
        // The API wraps the payload in `data`; unwrap it. The shape already
        // matches StudentAnalytics (both derived from the shared route schema).
        return response.data;
      },
    }),
    newStudentNote: build.mutation<
      StudentsPostNoteNewRouteResponse,
      {
        studentId: string;
        note: string;
        attributes?: Omit<StudentsPostNoteNewRouteBodyInput, 'note'>;
      }
    >({
      query(arg) {
        const { studentId, note, attributes } = arg;

        return {
          url: `students/${studentId}/notes/new`,
          body: {
            note,
            ...attributes,
          },
          method: 'POST',
        };
      },
      invalidatesTags: ['UserNotes'],
    }),
    getStudentNotes: build.query<
      StudentsGetNotesQueryRouteResponse,
      {
        studentId: string;
        active?: boolean;
        billingRelated?: boolean;
        limit?: number | 'all';
      }
    >({
      query(arg) {
        const { studentId, active, billingRelated, limit } = arg;

        return {
          url: `students/${studentId}/notes/query`,
          params: { active, billingRelated, limit },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'UserNotes' as const, id })), 'UserNotes']
          : ['UserNotes'],
    }),
    updateStudentNote: build.mutation<
      StudentsPutNoteUpdateRouteResponse,
      {
        userNoteId: string;
        newAttributes: StudentsPutNoteUpdateRouteBodyInput;
      }
    >({
      query(arg) {
        const { userNoteId, newAttributes } = arg;

        return {
          url: `students/notes/${userNoteId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      // Every user note should be invalidated if the due date of the note is updated, as
      // a note without a due date could gain a due date; in which case it should show
      // up in the follow up page
      invalidatesTags: (_result, _error, arg) =>
        arg.newAttributes.dueDatetime ? ['UserNotes'] : [{ type: 'UserNotes', id: arg.userNoteId }],
    }),
    deleteStudentNote: build.mutation<StudentsDeleteNoteRouteResponse, { userNoteId: string }>({
      query(arg) {
        return {
          url: `students/notes/${arg.userNoteId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'UserNotes', id: arg.userNoteId }],
    }),
    updateCredits: build.mutation<
      StudentsPutCreditsUpdateRouteResponse,
      {
        studentId: string;
        subject: string;
        courseId: string;
        streamId?: StudentsPutCreditsUpdateRouteBody['streamId'];
        credits: Omit<StudentsPutCreditsUpdateRouteBody, 'streamId'>;
      }
    >({
      query(arg) {
        const { studentId, subject, courseId, streamId, credits } = arg;
        // Named key by key, since the route refuses a key its body does not declare
        const body: StudentsPutCreditsUpdateRouteBody = {
          streamId,
          termCredits: credits.termCredits,
          extraCredits: credits.extraCredits,
        };

        return {
          url: `students/${studentId}/credits/${subject}/${courseId}/update`,
          body,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'Students', id: arg.studentId }],
    }),
    closeCourseState: build.mutation<
      StudentsPutCourseStateCloseRouteResponse,
      {
        studentId: string;
        subject: string;
        courseId: string;
        streamId?: StudentsPutCourseStateCloseRouteBody['streamId'];
      }
    >({
      query(arg) {
        const { studentId, subject, courseId, streamId } = arg;
        const body: StudentsPutCourseStateCloseRouteBody = { streamId };

        return {
          url: `students/${studentId}/courseStates/${subject}/${courseId}/close`,
          body,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [{ type: 'Students', id: arg.studentId }],
    }),
    newInvoices: build.mutation<
      StudentsPostInvoiceNewRouteResponse,
      {
        studentIds: string[];
        auto?: boolean;
      }
    >({
      query(arg) {
        const { studentIds, auto } = arg;

        return {
          url: 'students/invoices/new',
          method: 'POST',
          body: { studentIds, auto },
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        ...arg.studentIds.map(studentId => ({ type: 'Students' as const, id: studentId })),
      ],
    }),
    getStudentActivityLog: build.query<
      StudentsGetActivityLogQueryRouteResponse,
      {
        teacherId?: string;
        studentId?: string;
        creationDatetimeMin?: number;
      }
    >({
      query(arg) {
        const { teacherId, studentId, creationDatetimeMin } = arg;

        return {
          url: 'students/activityLog/query',
          params: {
            teacherId,
            studentId,
            creationDatetimeMin,
          },
          method: 'GET',
        };
      },
      providesTags: result =>
        result
          ? [
              ...result.data.map(({ studentId }) => ({
                type: 'StudentActivityLog' as const,
                id: studentId,
              })),
              'StudentActivityLog',
            ]
          : ['StudentActivityLog'],
    }),
    updateStudentActivityLog: build.mutation<
      StudentsPutActivityLogUpdateRouteResponse,
      {
        activityLogEntryId: string;
        studentId: string;
        newAttributes: StudentsPutActivityLogUpdateRouteBodyInput;
      }
    >({
      query(arg) {
        const { activityLogEntryId, newAttributes } = arg;

        return {
          url: `students/activityLog/${activityLogEntryId}/update`,
          body: newAttributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'StudentActivityLog', id: arg.studentId },
      ],
    }),
    getStudentLessonEnrolments: build.query<
      StudentsGetLessonsQueryRouteResponse,
      {
        studentId: string;
        queryStartDatetime?: number;
        queryEndDatetime?: number;
        limit?: number | 'all';
      }
    >({
      query(arg) {
        const { studentId, queryStartDatetime, queryEndDatetime, limit } = arg;
        return {
          url: `students/${studentId}/lessons/query`,
          method: 'GET',
          params: {
            queryStartDatetime,
            queryEndDatetime,
            limit,
          },
        };
      },
      providesTags: (_result, _error, arg) => [{ type: 'LessonEnrolments', id: arg.studentId }],
    }),
    getStudentOutstandingInvoices: build.query<
      StudentsGetOutstandingInvoicesQueryRouteResponse,
      { studentId: string }
    >({
      query: arg => `students/${arg.studentId}/outstandingInvoices/query`,
    }),
  }),
});

export const {
  useNewStudentMutation,
  useLazyGetStudentQuery,
  useGetStudentQuery,
  useGetStudentsQuery,
  useUpdateStudentMutation,
  useUpdateStudentOnboardingMutation,
  useGetStudentAnalyticsQuery,
  useDeleteStudentNoteMutation,
  useGetStudentNotesQuery,
  useNewStudentNoteMutation,
  useUpdateStudentNoteMutation,
  useUpdateCreditsMutation,
  useCloseCourseStateMutation,
  useGetStudentActivityLogQuery,
  useUpdateStudentActivityLogMutation,
  useNewInvoicesMutation,
  useGetStudentOutstandingInvoicesQuery,
  useGetStudentLessonEnrolmentsQuery,
} = studentsApi;
